import React, { useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Stage from '../components/Stage';
import Closet from '../components/Closet';
import Notification from '../components/Notification';
import WalletRequired from '../components/WalletRequired';
import { useWallet } from '../contexts/WalletContext';
import { Save, ArrowLeft, Edit3, Eye } from 'lucide-react';

export default function OutfitBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { publicKey } = useWallet();
  const [loadedModels, setLoadedModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [notification, setNotification] = useState({ message: '', type: 'info', isVisible: false });
  const [isSaving, setIsSaving] = useState(false);
  const [outfitName, setOutfitName] = useState('');
  const [outfitDescription, setOutfitDescription] = useState('');
  const [outfitTags, setOutfitTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const stageRef = useRef(null);

  // Check if we're editing or viewing an existing outfit
  const editMode = searchParams.get('edit');
  const viewMode = searchParams.get('view');
  const isEditing = !!editMode;
  const isViewing = !!viewMode;

  // Load existing outfit data if editing or viewing
  React.useEffect(() => {
    if (editMode || viewMode) {
      try {
        const savedOutfits = JSON.parse(localStorage.getItem('outfits') || '[]');
        const outfit = savedOutfits.find(o => o.id === editMode || o.id === viewMode);
        if (outfit) {
          console.log(`[OutfitBuilder] Loading outfit:`, outfit);
          setOutfitName(outfit.name);
          setOutfitDescription(outfit.description);
          setOutfitTags(outfit.tags || []);
          // Load the models from the saved outfit
          if (outfit.models) {
            console.log(`[OutfitBuilder] Loading ${outfit.models.length} models with transforms:`, outfit.models);
            outfit.models.forEach(model => {
              if (model.currentTransform) {
                console.log(`[OutfitBuilder] Model ${model.id} has saved transform:`, model.currentTransform);
              } else {
                console.log(`[OutfitBuilder] Model ${model.id} has NO saved transform`);
              }
            });
            setLoadedModels(outfit.models);
          }
        }
      } catch (error) {
        console.error('Error loading outfit:', error);
        setNotification({
          message: 'Error loading outfit data',
          type: 'error',
          isVisible: true
        });
      }
    }
  }, [editMode, viewMode]);

  const handleModelLoad = useCallback((modelId, scene, metadata) => {
    console.log(`[OutfitBuilder] Model ${modelId} loaded successfully:`, metadata);
    
    // Update model with metadata if available
    if (metadata && metadata.boundingBox) {
      setLoadedModels(prev => prev.map(model => 
        model.id === modelId 
          ? { 
              ...model, 
              metadata, 
              boundingBox: metadata.boundingBox,
              // Store initial transform data
              initialTransform: metadata.initialTransform,
              // CRITICAL: Set currentTransform to initialTransform so the position is saved
              // even if the user doesn't manually move this piece
              currentTransform: model.currentTransform || metadata.initialTransform
            }
          : model
      ));
    }
  }, []);

  const handleModelError = useCallback((modelId, error) => {
    console.error(`[OutfitBuilder] Failed to load model ${modelId}:`, error);
    setNotification({
      message: `Failed to load model: ${error.message}`,
      type: 'error',
      isVisible: true
    });
  }, []);

  const handleModelSelect = useCallback((modelId) => {
    console.log(`[OutfitBuilder] Model selected: ${modelId}`);
    setSelectedModelId(modelId);
    if (modelId) {
      const model = loadedModels.find(m => m.id === modelId);
      if (model) {
        setNotification({
          message: `${model.name} selected`,
          type: 'info',
          isVisible: true
        });
      }
    } else {
      setNotification({
        message: 'Selection cleared',
        type: 'info',
        isVisible: true
      });
    }
  }, [loadedModels]);

  // CRITICAL: This is where transforms are persisted in the main state
  const handleTransformChange = useCallback((transformData) => {
    if (selectedModelId && transformData.transformUpdated) {
      console.log(`[OutfitBuilder] Transform updated for ${selectedModelId}:`, transformData);
      
      // Update the model's transform data in the main state
      setLoadedModels(prev => {
        const updatedModels = prev.map(model => 
          model.id === selectedModelId 
            ? { 
                ...model, 
                // Store the current transform
                currentTransform: {
                  position: transformData.position,
                  rotation: transformData.rotation,
                  scale: transformData.scale,
                  timestamp: Date.now()
                },
                // Keep the initial transform for reference
                initialTransform: model.initialTransform
              }
            : model
        );
        
        // Log the updated model for debugging
        const updatedModel = updatedModels.find(m => m.id === selectedModelId);
        console.log(`[OutfitBuilder] Updated model ${selectedModelId} with transform:`, updatedModel?.currentTransform);
        
        return updatedModels;
      });
      
      // Get the current model name for the notification
      const currentModel = loadedModels.find(m => m.id === selectedModelId);
      setNotification({
        message: `Transform updated for ${currentModel?.name || 'model'}`,
        type: 'success',
        isVisible: true
      });
    }
  }, [selectedModelId]); // Remove loadedModels from dependency array to prevent recreation

  const addModelToStage = useCallback((item) => {
    console.log(`[OutfitBuilder] Adding model to stage:`, item);
    console.log(`[OutfitBuilder] Thumbnail data available:`, {
      thumbnail: item.thumbnail,
      thumbnailUrl: item.thumbnailUrl,
      image: item.image
    });
    
    setLoadedModels(prev => {
      // Check if this exact piece is already on the stage
      const alreadyAdded = prev.some(model => model.id === item.id);
      
      if (alreadyAdded) {
        console.log(`[OutfitBuilder] Piece ${item.name} is already on stage`);
        return prev; // Don't add duplicates
      }
      
      // Add the new piece - allow multiple pieces even from same category
      // Include all relevant piece data for outfit saving
      const newModel = {
        id: item.id,
        modelUrl: item.modelUrl,
        category: item.category,
        name: item.name,
        price: item.price || 0, // Include price
        color: item.color,
        description: item.description,
        tags: item.tags || [],
        creator: item.creator || item.brand,
        creatorId: item.creatorId,
        originalCreator: item.originalCreator, // Include full creator object
        holder: item.holder,
        holderId: item.holderId,
        holderAddress: item.holderAddress || item.holder_wallet,
        createdAt: item.createdAt,
        thumbnail: item.thumbnail,
        thumbnailUrl: item.thumbnailUrl,
        image: item.image,
        // Don't set position/rotation/scale here - let ModelLoader handle initial positioning
        // The transforms will be set by the normalization process
      };
      
      console.log(`[OutfitBuilder] Model being added with data:`, newModel);
      return [...prev, newModel];
    });
    
    setNotification({
      message: `${item.name} added to 3D stage`,
      type: 'success',
      isVisible: true
    });
  }, []);

  const removeModelFromStage = useCallback((modelId) => {
    const model = loadedModels.find(m => m.id === modelId);
    setLoadedModels(prev => prev.filter(model => model.id !== modelId));
    
    // Clear selection if the removed model was selected
    if (selectedModelId === modelId) {
      setSelectedModelId(null);
    }
    
    if (model) {
      setNotification({
        message: `${model.name} removed from 3D stage`,
        type: 'info',
        isVisible: true
      });
    }
  }, [loadedModels, selectedModelId]);

  const clearAllModels = useCallback(() => {
    console.log(`[OutfitBuilder] Clearing all models`);
    setLoadedModels([]);
    setSelectedModelId(null);
    setNotification({
      message: 'All items cleared from 3D stage',
      type: 'info',
      isVisible: true
    });
  }, []);

  const closeNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  }, []);

  // Tag management functions
  const addTag = () => {
    const tag = newTag.trim().toLowerCase();
    if (tag && !outfitTags.includes(tag) && outfitTags.length < 5) {
      setOutfitTags([...outfitTags, tag]);
      setNewTag('');
    } else if (outfitTags.length >= 5) {
      setNotification({
        message: 'You can only add up to 5 tags per outfit',
        type: 'error',
        isVisible: true
      });
    }
  };

  const removeTag = (tagToRemove) => {
    setOutfitTags(outfitTags.filter(tag => tag !== tagToRemove));
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (outfitTags.length < 5) {
        addTag();
      }
    }
  };

  // Save outfit functionality
  const handleSaveOutfit = async () => {
    if (!outfitName.trim()) {
      setNotification({
        message: 'Please enter an outfit name',
        type: 'error',
        isVisible: true
      });
      return;
    }

    if (loadedModels.length === 0) {
      setNotification({
        message: 'Please add at least one item to your outfit',
        type: 'error',
        isVisible: true
      });
      return;
    }

    setIsSaving(true);

    try {
      
      const outfitData = {
        id: editMode || Date.now().toString(),
        name: outfitName.trim(),
        description: outfitDescription.trim(),
        tags: outfitTags,
        creator_wallet: publicKey?.toString() || null,
        models: loadedModels.map(model => ({
          id: model.id,
          modelUrl: model.modelUrl,
          category: model.category,
          name: model.name,
          color: model.color,
          description: model.description,
          price: model.price,
          tags: model.tags,
          creator: model.creator,
          creatorId: model.creatorId,
          originalCreator: model.originalCreator,
          holder: model.holder,
          holderId: model.holderId,
          holderAddress: model.holderAddress,
          createdAt: model.createdAt,
          thumbnail: model.thumbnail,
          thumbnailUrl: model.thumbnailUrl,
          image: model.image,
          currentTransform: model.currentTransform,
          initialTransform: model.initialTransform
        })),
        pieces: loadedModels.length,
        likes: 0,
        isFavorite: false,
        rating: null,
        createdAt: editMode ? undefined : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isNew: !editMode
      };

      // Get existing outfits
      const existingOutfits = JSON.parse(localStorage.getItem('outfits') || '[]');
      
      let updatedOutfits;
      if (editMode) {
        // Update existing outfit, preserve original creator_wallet if it exists
        updatedOutfits = existingOutfits.map(o => {
          if (o.id === editMode) {
            return {
              ...outfitData,
              creator_wallet: o.creator_wallet || outfitData.creator_wallet
            };
          }
          return o;
        });
      } else {
        // Add new outfit
        updatedOutfits = [outfitData, ...existingOutfits];
      }

      // Save to localStorage
      localStorage.setItem('outfits', JSON.stringify(updatedOutfits));

      // Debug: Log the saved outfit data
      console.log('Outfit saved successfully!');
      console.log('Outfit data:', outfitData);
      console.log('All outfits in localStorage:', updatedOutfits.length);

      setNotification({
        message: editMode ? 'Outfit updated successfully!' : 'Outfit saved successfully!',
        type: 'success',
        isVisible: true
      });

      // Close save modal
      setShowSaveModal(false);

      // Navigate to outfits page after a short delay
      setTimeout(() => {
        navigate('/outfits');
      }, 1500);

    } catch (error) {
      console.error('Error saving outfit:', error);
      setNotification({
        message: 'Error saving outfit. Please try again.',
        type: 'error',
        isVisible: true
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackToOutfits = () => {
    navigate('/outfits');
  };



  return (
    <WalletRequired 
      message="You need to connect your wallet to create and save outfits."
      action="create outfits"
    >
      <div className="min-h-screen bg-black text-[#E7EDF5]">
        {/* Notification */}
        <Notification
          message={notification.message}
          type={notification.type}
        isVisible={notification.isVisible}
        onClose={closeNotification}
      />
      
      <main className="mx-auto max-w-[1480px] px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToOutfits}
                className="p-2 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                title="Back to Outfits"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-[#E7EDF5] mb-2">
                  {isEditing ? 'Edit Outfit' : isViewing ? 'View Outfit' : 'Outsera Wardrobe'}
                </h1>
                <p className="text-[#A8B3C2]">
                  {isEditing ? 'Modify your outfit design' : isViewing ? 'Preview your outfit' : 'Design and visualize your perfect outfit in 3D'}
                </p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {!isViewing && (
                <button
                  onClick={() => setShowSaveModal(true)}
                  disabled={loadedModels.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-[#2a2a2a] hover:bg-[#3a3a3a] disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-all duration-200 font-medium"
                >
                  <Save size={18} />
                  {isEditing ? 'Update Outfit' : 'Save Outfit'}
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Grid Layout */}
        <div className="grid grid-cols-[minmax(0,1fr)] gap-6">
          {/* Stage Section */}
          <Stage 
            loadedModels={loadedModels}
            onModelLoad={handleModelLoad}
            onModelError={handleModelError}
            selectedModelId={selectedModelId}
            onModelSelect={handleModelSelect}
            onTransformChange={handleTransformChange}
            onClearStage={clearAllModels}
            onRemoveModel={removeModelFromStage}
            ref={stageRef}
          />
          
          {/* Closet Section */}
          <Closet 
            onAddToStage={addModelToStage}
            onRemoveFromStage={removeModelFromStage}
            onClearStage={clearAllModels}
            loadedModels={loadedModels}
          />
        </div>
      </main>
      

      {/* Save Outfit Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#111111] rounded-2xl p-6 w-full max-w-md mx-4 border border-[#2a2a2a]">
            <h3 className="text-xl font-semibold text-white mb-4">
              {isEditing ? 'Update Outfit' : 'Save New Outfit'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Outfit Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={outfitName}
                  onChange={(e) => setOutfitName(e.target.value)}
                  placeholder="Enter outfit name"
                  className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={outfitDescription}
                  onChange={(e) => setOutfitDescription(e.target.value)}
                  placeholder="Describe your outfit..."
                  rows={3}
                  className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tags
                </label>
                
                {/* Tags Display */}
                {outfitTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {outfitTags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-white/20 text-white rounded-full text-sm border border-white/30"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="text-white hover:text-gray-300 transition-colors"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Tag Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={handleTagKeyPress}
                    placeholder={outfitTags.length >= 5 ? "Maximum 5 tags reached" : "Add a tag..."}
                    disabled={outfitTags.length >= 5}
                    className={`flex-1 px-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white ${
                      outfitTags.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    disabled={outfitTags.length >= 5}
                    className={`px-4 py-2 rounded-lg transition-colors font-medium ${
                      outfitTags.length >= 5 
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                        : 'bg-white hover:bg-gray-200 text-gray-900'
                    }`}
                  >
                    Add
                  </button>
                </div>
                
                {/* Tag Counter */}
                <div className="text-xs text-gray-400 mt-1">
                  {outfitTags.length}/5 tags used
                </div>
              </div>
              
              <div className="text-sm text-gray-400">
                <p>Items in outfit: {loadedModels.length}</p>
                <p>Item categories: {loadedModels.map(m => m.category).join(', ')}</p>
                {outfitTags.length > 0 && (
                  <p>Tags: {outfitTags.join(', ')}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setShowSaveModal(false)}
                className="flex-1 px-4 py-2 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#2a2a2a] hover:border-[#3a3a3a] text-white rounded-lg transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveOutfit}
                disabled={isSaving || !outfitName.trim()}
                className="flex-1 px-4 py-2 bg-[#2a2a2a] hover:bg-[#3a3a3a] disabled:bg-[#1a1a1a] disabled:cursor-not-allowed border border-[#3a3a3a] disabled:border-[#2a2a2a] text-white rounded-lg transition-all duration-200 font-medium"
              >
                {isSaving ? 'Saving...' : (isEditing ? 'Update' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </WalletRequired>
  );
}
